import { useState } from "react";
import { Loader2, CheckCircle2, Mail, MapPin, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Us"
        description="Get in touch with the Enhancing Your World team. Visit our Newport, KY wellness center or Verona, KY retreat center, or send us a message online."
        path="/contact"
      />
      <PageHero label="Contact" title="Reach out to the ecosystem." testid="contact-hero" />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-[1fr_1.2fr] gap-12">
        <div>
          <h2 className="font-serif text-3xl text-[#1A242B]">We'd love to hear from you.</h2>
          <p className="text-[#4A5D6B] mt-4 leading-relaxed">
            Whether you're exploring membership, wellness services, becoming an Enhancer, or stewardship —
            send us a note and we'll help you find your way in.
          </p>
          <div className="mt-8 space-y-4" data-testid="contact-details">
            <a href="tel:+18599795505" className="flex items-center gap-3 text-[#4A5D6B] hover:text-[#144F75]" data-testid="contact-phone-link"><Phone size={18} className="text-[#144F75]" /> 859-979-5505</a>
            <a href="mailto:info@enhancingyourworld.com" className="flex items-center gap-3 text-[#4A5D6B] hover:text-[#144F75]" data-testid="contact-email-link"><Mail size={18} className="text-[#144F75]" /> info@enhancingyourworld.com</a>
            <div className="flex items-center gap-3 text-[#4A5D6B]"><MapPin size={18} className="text-[#144F75]" /> Enhancing Your World Ecosystem</div>
          </div>
        </div>
        <div className="eyw-card p-8">
          {done ? (
            <div className="text-center py-10" data-testid="contact-success">
              <CheckCircle2 size={48} className="text-[#144F75] mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-[#1A242B]">Message sent.</h3>
              <p className="text-[#4A5D6B] mt-2">Thank you for reaching out. We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Name" value={form.name} onChange={set("name")} data-testid="contact-name" className="eyw-input" />
                <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="contact-email" className="eyw-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input placeholder="Callback phone (optional)" value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="eyw-input" />
                <input placeholder="Subject" value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="eyw-input" />
              </div>
              <textarea required rows={5} placeholder="Your message" value={form.message} onChange={set("message")} data-testid="contact-message" className="eyw-input w-full resize-none" />
              {error && <p className="text-sm text-red-600" data-testid="contact-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="contact-submit" className="eyw-btn w-full disabled:opacity-60">
                {loading && <Loader2 className="animate-spin" size={18} />} Send Message
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
